import { useEffect, useMemo, useState } from 'react'
import {
  Stethoscope, Loader2, AlertTriangle, Users, ChevronLeft, ChevronRight,
  Calendar, Receipt, ExternalLink, Wallet,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { CARD } from '../components/ui.jsx'
import { useDoctors } from '../lib/doctors.js'
import { fmtDateOnly } from '../lib/dt.js'
import { lineActualTotal, lineActualClinic, rowsIncome, thb } from '../lib/billing.js'
import { openPatientChart } from '../lib/sso.js'

/**
 * Treatment tab — per-operation, per-day list of the patient treatments done
 * at the Tooth Taxi mobile branch (branch_id 9, fixed server-side).
 *
 * Data comes from the SSDC Xano `/treatment?type=dashboard` feed via the
 * `/api/treatment` proxy. The `date` is the operation day (a day selector
 * appears for multi-day operations); pagination + a doctor filter (button
 * group) are server-side so the list stays consistent across pages.
 *
 * Each patient card shows its price×unit total; the banner up top shows the
 * whole operation's income (all days, all doctors).
 */

const PER_PAGE_OPTIONS = [25, 50, 100]
const ROSTER_PER_PAGE  = 200   // one unfiltered fetch to learn the day's doctors

// Treatment fullnames sometimes carry leading "★" priority markers — strip
// them for display (mirrors the doctor portal's stripStars).
function stripStars(s) {
  return (s || '').replace(/[★☆*]/g, '').trim()
}
function nameInitial(name) {
  const n = stripStars(name)
  return n ? n[0].toUpperCase() : '?'
}

function pickRows(res) {
  if (!res) return []
  if (Array.isArray(res)) return res
  return res.items || res.data || res.result || []
}

// Read the standard Xano paging envelope when present; otherwise infer from
// the page we got back (a full page implies there may be a next one).
function pickMeta(res, rows, page, perPage) {
  if (res && !Array.isArray(res) && (res.itemsTotal != null || res.pageTotal != null)) {
    return {
      itemsTotal: res.itemsTotal ?? null,
      pageTotal:  res.pageTotal ?? null,
      curPage:    res.curPage ?? page,
      hasPrev:    (res.curPage ?? page) > 1,
      hasNext:    res.nextPage != null ? !!res.nextPage : (res.pageTotal != null ? (res.curPage ?? page) < res.pageTotal : rows.length >= perPage),
    }
  }
  return {
    itemsTotal: null, pageTotal: null, curPage: page,
    hasPrev: page > 1, hasNext: rows.length >= perPage,
  }
}

// Operation days as a YYYY-MM-DD list (start..end inclusive). Anchored to UTC
// so the day math never shifts under the local (UTC+7) timezone.
function opDays(op) {
  const start = op?.start_date
  const end   = op?.end_date || op?.start_date
  if (!start) return []
  const days = []
  let t = Date.parse(start + 'T00:00:00Z')
  const last = Date.parse(end + 'T00:00:00Z')
  for (let i = 0; i < 60 && t <= last; i++) {
    days.push(new Date(t).toISOString().slice(0, 10))
    t += 86400000
  }
  return days.length ? days : [start]
}

export default function TreatmentTab({ op }) {
  const days = useMemo(() => opDays(op), [op])
  const [date, setDate]         = useState(days[0] || op?.start_date || '')
  const [doctorId, setDoctorId] = useState('')        // '' = all
  const [perPage, setPerPage]   = useState(50)
  const [page, setPage]         = useState(1)

  const [res, setRes] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)

  // Day roster — a separate unfiltered fetch so the doctor filter buttons stay
  // stable no matter which doctor is selected (fixes the "collapses to one"
  // bug where options were derived from the filtered rows).
  const [rosterIds, setRosterIds] = useState([])

  const doctors = useDoctors()

  // Reset to page 1 whenever the day / doctor / page-size changes.
  useEffect(() => { setPage(1) }, [date, doctorId, perPage])

  // Main paginated (optionally doctor-filtered) fetch.
  useEffect(() => {
    if (!date) return
    let cancelled = false
    setLoading(true); setErr(null)
    api.treatment.list({ date, page, per_page: perPage, doctor_id: doctorId || undefined })
      .then((d) => { if (!cancelled) setRes(d) })
      .catch((e) => { if (!cancelled) { setErr(e.message); setRes(null) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [date, doctorId, perPage, page])

  // Roster fetch — once per day, unfiltered, to enumerate the doctors present.
  useEffect(() => {
    if (!date) { setRosterIds([]); return }
    let cancelled = false
    api.treatment.list({ date, page: 1, per_page: ROSTER_PER_PAGE })
      .then((d) => {
        if (cancelled) return
        const ids = new Set()
        for (const tx of pickRows(d)) if (tx?.doctor_id) ids.add(Number(tx.doctor_id))
        setRosterIds([...ids])
      })
      .catch(() => { if (!cancelled) setRosterIds([]) })
    return () => { cancelled = true }
  }, [date])

  const rows = useMemo(() => pickRows(res), [res])
  const meta = useMemo(() => pickMeta(res, rows, page, perPage), [res, rows, page, perPage])

  // Group the page's rows by patient so multiple treatments collapse into one
  // card. Order is preserved by first appearance.
  const patients = useMemo(() => {
    const byId = new Map()
    for (const tx of rows) {
      if (tx?.active === false) continue
      const pid = tx.patient_id ?? `row-${tx.id}`
      if (!byId.has(pid)) {
        byId.set(pid, {
          patient_id: tx.patient_id,
          fullname: stripStars(tx.fullname),
          HN: tx.HN,
          pt_status: tx.pt_status,
          doctor_id: tx.doctor_id,
          treatments: [],
        })
      }
      byId.get(pid).treatments.push(tx)
    }
    return [...byId.values()]
  }, [rows])

  const pageIncome = useMemo(() => rowsIncome(rows), [rows])

  // Doctor filter options: doctors assigned to this operation day, merged with
  // the day's actual roster (covers swaps / unassigned days). Stable — never
  // shrinks to just the selected doctor.
  const doctorOptions = useMemo(() => {
    const ids = new Set(rosterIds.map(Number))
    for (const dd of op?.doctor_days || []) {
      if (dd.doctor_xano_id && (!date || dd.day_date === date)) ids.add(Number(dd.doctor_xano_id))
    }
    // Always include the currently-selected doctor so its button stays visible.
    if (doctorId) ids.add(Number(doctorId))
    return [...ids].map((id) => ({
      id,
      code: doctors?.byId?.get(id)?.code || `#${id}`,
    })).sort((a, b) => a.code.localeCompare(b.code))
  }, [op, date, rosterIds, doctorId, doctors])

  return (
    <div className="space-y-4">
      <OperationIncomeBanner op={op} />

      <Toolbar
        days={days} date={date} setDate={setDate}
        doctorOptions={doctorOptions} doctorId={doctorId} setDoctorId={setDoctorId}
        perPage={perPage} setPerPage={setPerPage}
        total={meta.itemsTotal} pageIncome={pageIncome} loading={loading}
      />

      {err && (
        <div className={`${CARD} p-5 text-rose-700 text-[13px] inline-flex items-center gap-2`}>
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {loading && !res ? (
        <div className={`${CARD} p-8 text-center text-zinc-500 text-[13px]`}>
          <Loader2 size={16} className="inline animate-spin mr-1.5" /> Loading…
        </div>
      ) : !err && patients.length === 0 ? (
        <div className={`${CARD} p-8 text-center`}>
          <Stethoscope size={26} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-[13.5px] text-zinc-700 font-medium">ยังไม่มีการรักษาในวันนี้</p>
          <p className="text-[12px] text-zinc-500 mt-1">
            {date ? `วันที่ ${fmtDateOnly(date)}` : ''}{doctorId ? ' · ลองเลือกหมอทั้งหมด' : ''}
          </p>
        </div>
      ) : (
        <div className={`grid gap-2.5 ${loading ? 'opacity-60 transition-opacity' : ''}`}>
          {patients.map((p) => (
            <PatientCard key={p.patient_id ?? p.fullname} p={p} doctors={doctors} />
          ))}
        </div>
      )}

      {(meta.hasPrev || meta.hasNext) && (
        <Pager meta={meta} page={page} setPage={setPage} count={patients.length} loading={loading} />
      )}
    </div>
  )
}


/** Whole-operation income (all days) — the authoritative Total + Clinic figure
 *  from the chart/revenue aggregate, scoped to this operation's date range. */
function OperationIncomeBanner({ op }) {
  const [data, setData] = useState(null)
  const [err, setErr]   = useState(false)

  useEffect(() => {
    if (!op?.start_date) return
    let cancelled = false
    setData(null); setErr(false)
    api.revenue.range(op.start_date, op.end_date || op.start_date)
      .then((d) => {
        if (cancelled) return
        let total = 0, clinic = 0
        for (const day of d.days || []) { total += Number(day.total) || 0; clinic += Number(day.clinic) || 0 }
        setData({ total, clinic })
      })
      .catch(() => { if (!cancelled) setErr(true) })
    return () => { cancelled = true }
  }, [op?.start_date, op?.end_date])

  return (
    <div className="rounded-2xl ring-1 ring-emerald-200/80 bg-gradient-to-br from-emerald-50 to-white px-5 py-4 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
        <Wallet size={20} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700/80">รายได้รวมทั้งงาน (Total)</div>
        <div className="text-[26px] font-bold text-emerald-900 leading-tight tabular-nums">
          {data ? thb(data.total) : err ? <span className="text-[15px] text-zinc-400 font-medium">—</span> : <Loader2 size={18} className="inline animate-spin text-emerald-400" />}
        </div>
      </div>
      {data && (
        <div className="ml-auto text-right leading-snug">
          <div className="text-[18px] font-bold text-emerald-700 tabular-nums">{thb(data.clinic)}</div>
          <div className="text-[11px] text-emerald-800/70">ส่วนของคลินิก (Clinic)</div>
        </div>
      )}
    </div>
  )
}


function Toolbar({ days, date, setDate, doctorOptions, doctorId, setDoctorId, perPage, setPerPage, total, pageIncome, loading }) {
  const selCls = 'h-9 rounded-lg ring-1 ring-zinc-200 bg-white px-2.5 text-[13px] text-zinc-800 focus:outline-none focus:ring-2 focus:ring-amber-400'
  return (
    <div className={`${CARD} p-3 space-y-3`}>
      {/* Row 1 — day, page size, totals */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
        <label className="inline-flex items-center gap-2 text-[12px] text-zinc-500 font-medium">
          <Calendar size={14} className="text-zinc-400" />
          {days.length > 1 ? (
            <select className={selCls} value={date} onChange={(e) => setDate(e.target.value)}>
              {days.map((d) => <option key={d} value={d}>{fmtDateOnly(d)}</option>)}
            </select>
          ) : (
            <span className="text-[13px] text-zinc-800 font-semibold tabular-nums">{date ? fmtDateOnly(date) : '—'}</span>
          )}
        </label>

        <label className="inline-flex items-center gap-2 text-[12px] text-zinc-500 font-medium">
          ต่อหน้า
          <select className={selCls} value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
            {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>

        <div className="ml-auto inline-flex items-center gap-3 text-[12px] text-zinc-500">
          {loading && <Loader2 size={13} className="animate-spin text-amber-500" />}
          {total != null && (
            <span className="inline-flex items-center gap-1.5">
              <Users size={13} className="text-zinc-400" />
              <span className="tabular-nums font-medium text-zinc-700">{total}</span> รายการ
            </span>
          )}
          <span className="inline-flex items-center gap-1.5" title="รวมราคา × จำนวน เฉพาะหน้านี้">
            <Receipt size={13} className="text-zinc-400" />
            <span className="tabular-nums font-medium text-zinc-700">{thb(pageIncome)}</span>
            <span className="text-zinc-400">/ หน้า</span>
          </span>
        </div>
      </div>

      {/* Row 2 — doctor filter as a button group */}
      <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-400 mr-0.5">หมอ</span>
        <FilterChip active={!doctorId} onClick={() => setDoctorId('')}>ทั้งหมด</FilterChip>
        {doctorOptions.map((d) => (
          <FilterChip key={d.id} active={String(doctorId) === String(d.id)} onClick={() => setDoctorId(String(d.id))}>
            {d.code}
          </FilterChip>
        ))}
        {doctorOptions.length === 0 && (
          <span className="text-[12px] text-zinc-400 italic">ไม่มีข้อมูลหมอสำหรับวันนี้</span>
        )}
      </div>
    </div>
  )
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'h-7 px-3 rounded-full text-[12.5px] font-medium transition-colors ' +
        (active
          ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/25'
          : 'bg-white ring-1 ring-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900')
      }
    >
      {children}
    </button>
  )
}


function PatientCard({ p, doctors }) {
  const docCode = p.doctor_id ? (doctors?.byId?.get(Number(p.doctor_id))?.code || `#${p.doctor_id}`) : null

  // Actual (post-insurance) figures, summed across every billing line — Total
  // and the clinic's share — mirroring the operation income banner.
  const { total, clinic } = useMemo(() => {
    let total = 0, clinic = 0
    for (const tx of p.treatments) {
      for (const b of tx.billing || []) {
        total  += lineActualTotal(b)
        clinic += lineActualClinic(b)
      }
    }
    return { total, clinic }
  }, [p])

  // Treatment chips — appointment labels deduped with a count.
  const chips = []
  const seen = new Map()
  for (const tx of p.treatments) {
    const al = tx.appointmentlist || {}
    const label = al.detail || tx.detail
    if (!label) continue
    if (seen.has(label)) seen.get(label).count += 1
    else { const e = { label, type: al.type, count: 1 }; seen.set(label, e); chips.push(e) }
  }
  const notes = [...new Set(p.treatments.map((tx) => (tx.detail || '').trim()).filter(Boolean))]

  // Billing line items (name + actual total), sorted by amount desc.
  const items = []
  for (const tx of p.treatments) {
    for (const b of tx.billing || []) {
      const label = (b.detail || b.billing_type?.name || b.billing_type?.detail || b.billing_type?.type || '').trim()
      const amt = lineActualTotal(b)
      if (label || amt) items.push({ id: b.id, label: label || 'รายการ', type: b.billing_type?.type, amt })
    }
  }
  items.sort((a, b) => b.amt - a.amt)

  // pt_status on the branch-9 feed is always "Tooth Taxi" (redundant here) —
  // hide that, but keep any genuine status (new / VIP / …).
  const showStatus = p.pt_status && !/tooth\s*taxi/i.test(p.pt_status)

  const clickable = !!p.patient_id
  const openChart = () => { if (clickable) openPatientChart(p.patient_id) }

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={openChart}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openChart() } } : undefined}
      title={clickable ? 'เปิดประวัติคนไข้ (Chart) ใน CRM' : undefined}
      className={
        `${CARD} group p-3.5 ` +
        (clickable
          ? 'cursor-pointer transition-all hover:ring-amber-300 hover:shadow-md hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400'
          : '')
      }
    >
      {/* Header — avatar · name + HN + tags · actual total */}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-100 to-orange-200 text-amber-800 flex items-center justify-center text-[13px] font-bold shrink-0">
          {nameInitial(p.fullname)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span className="text-[14px] font-semibold text-zinc-900 truncate">
              {p.fullname || `#${p.patient_id}`}
            </span>
            {p.HN && (
              <span className="text-[10.5px] font-mono text-zinc-400 shrink-0">HN {p.HN}</span>
            )}
            {showStatus && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ${statusTone(p.pt_status)}`}>{p.pt_status}</span>
            )}
            {p.treatments.length > 1 && (
              <span className="text-[9.5px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-1.5 py-0.5 rounded">
                ×{p.treatments.length}
              </span>
            )}
            {docCode && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 ring-1 ring-zinc-200 text-zinc-600">
                {docCode}
              </span>
            )}
            {clickable && (
              <ExternalLink size={12} className="text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            )}
          </div>

          {chips.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {chips.map((c, i) => (
                <span key={i} className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-50 ring-1 ring-zinc-200">
                  {c.type && <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">{c.type}</span>}
                  <span className="text-zinc-700 font-medium">{c.label}</span>
                  {c.count > 1 && <span className="text-zinc-400">×{c.count}</span>}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actual Total (+ clinic share) — label inline with its amount. */}
        <div className="shrink-0 pt-0.5 space-y-0.5">
          <div className="flex items-baseline justify-end gap-1.5">
            <span className="text-[10px] text-zinc-400">รวม</span>
            <span className={`text-[15px] font-bold tabular-nums ${total > 0 ? 'text-emerald-700' : 'text-zinc-300'}`}>
              {thb(total)}
            </span>
          </div>
          {clinic > 0 && clinic !== total && (
            <div className="flex items-baseline justify-end gap-1.5">
              <span className="text-[10px] text-zinc-400">คลินิก</span>
              <span className="text-[13px] font-semibold text-emerald-600/90 tabular-nums">{thb(clinic)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Body — billing items + notes, indented under the name; prices align to
          the card's right edge (same column as the total above). */}
      {(items.length > 0 || notes.length > 0) && (
        <div className="mt-2 pl-12">
          {items.length > 0 && (
            <ul className="space-y-0.5">
              {items.map((it, i) => (
                <li key={it.id || i} className="flex items-baseline justify-between gap-3 text-[11.5px]">
                  <span className="text-zinc-600 truncate inline-flex items-baseline gap-1.5">
                    {it.type && <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-300">{it.type}</span>}
                    {it.label}
                  </span>
                  <span className="text-zinc-500 tabular-nums shrink-0">{it.amt ? thb(it.amt) : '—'}</span>
                </li>
              ))}
            </ul>
          )}

          {notes.length > 0 && (
            <div className="mt-1.5 pl-2 border-l-2 border-zinc-200 text-[11.5px] text-zinc-500 italic leading-snug">
              {notes.join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


function Pager({ meta, page, setPage, count, loading }) {
  return (
    <div className="flex items-center justify-between gap-3 px-1">
      <span className="text-[12px] text-zinc-500">
        หน้า <span className="font-semibold text-zinc-700 tabular-nums">{meta.curPage}</span>
        {meta.pageTotal ? <> / <span className="tabular-nums">{meta.pageTotal}</span></> : null}
        <span className="text-zinc-400"> · {count} ในหน้านี้</span>
      </span>
      <div className="inline-flex items-center gap-1.5">
        <PagerBtn disabled={!meta.hasPrev || loading} onClick={() => setPage((n) => Math.max(1, n - 1))}>
          <ChevronLeft size={14} /> ก่อนหน้า
        </PagerBtn>
        <PagerBtn disabled={!meta.hasNext || loading} onClick={() => setPage((n) => n + 1)}>
          ถัดไป <ChevronRight size={14} />
        </PagerBtn>
      </div>
    </div>
  )
}

function PagerBtn({ disabled, onClick, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12.5px] font-medium ring-1 ring-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  )
}


function statusTone(status) {
  const s = (status || '').toLowerCase()
  if (s.includes('new') || s.includes('ใหม่')) return 'bg-emerald-50 ring-emerald-200 text-emerald-700'
  if (s.includes('vip')) return 'bg-amber-50 ring-amber-200 text-amber-700'
  return 'bg-zinc-50 ring-zinc-200 text-zinc-600'
}
